import { useEffect, useRef, useState } from "react";
import { Award, Factory, Leaf, Globe } from "lucide-react";

const stats = [
  { icon: Award, value: "19+", label: "Years Experience", color: "from-amber-500 to-orange-500" },
  { icon: Factory, value: "5+", label: "Quality Products", color: "from-forest to-emerald-600" },
  { icon: Leaf, value: "100%", label: "Eco-Friendly", color: "from-green-500 to-teal-500" },
  { icon: Globe, value: "Pan", label: "India Delivery", color: "from-blue-500 to-indigo-500" },
];

export default function About() {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVis(true); obs.disconnect(); } }, { threshold: 0.15 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  return (
    <section ref={ref} id="about" className="section-padding bg-[#F9FAFB] overflow-hidden" data-testid="about-section">
      <div className="container-custom">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Image Section */}
          <div className={`relative transition-all duration-1000 ${vis ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-12"}`}>
            <div className="relative z-10 group">
              <img 
                src="https://images.unsplash.com/photo-1742967418787-1d99f524b4de?crop=entropy&cs=srgb&fm=jpg&q=85&w=800" 
                alt="Coir production facility" 
                className="shadow-2xl w-full object-cover h-[400px] md:h-[520px] transition-transform duration-700 group-hover:scale-[1.02]" 
              />
              {/* Overlay gradient on hover */}
              <div className="absolute inset-0 bg-gradient-to-t from-navy/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            </div>
            
            {/* Decorative elements */}
            <div className="absolute -top-6 -left-6 w-32 h-32 bg-gradient-to-br from-forest/20 to-forest/5 -z-10" />
            <div className="absolute -bottom-6 -right-6 w-40 h-40 bg-gradient-to-br from-coir/20 to-coir/5 -z-10" />
            
            {/* Floating badge */}
            <div className={`absolute -bottom-4 -right-4 md:bottom-8 md:-right-8 bg-white shadow-xl p-4 md:p-6 z-20 transition-all duration-1000 delay-500 ${vis ? "opacity-100 scale-100" : "opacity-0 scale-90"}`}>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-forest to-emerald-600 flex items-center justify-center">
                  <Leaf className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-navy font-serif">100%</p>
                  <p className="text-xs text-slate-500 font-sans uppercase tracking-wider">Natural & Sustainable</p>
                </div>
              </div>
            </div>
          </div>

          {/* Content Section */}
          <div className={`transition-all duration-1000 delay-300 ${vis ? "opacity-100 translate-x-0" : "opacity-0 translate-x-12"}`}>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-[3px] bg-gradient-to-r from-forest to-coir" />
              <span className="text-forest font-semibold text-sm uppercase tracking-[0.2em] font-sans">About Us</span>
            </div>
            
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif text-navy mb-6 tracking-tight leading-tight">
              Leading Manufacturer of{" "}
              <span className="text-forest">Coconut Fiber</span> &{" "}
              <span className="text-coir">Coco Peat</span>
            </h2>
            
            <p className="text-slate-600 text-lg mb-6 leading-relaxed font-sans">
              Based in Andhra Pradesh, India, <strong className="text-navy">Sri Srinivasa Coir Industry</strong> has been a trusted manufacturer
              and supplier of premium coconut fiber and cocopeat products since 2006, serving both
              domestic and international markets.
            </p>
            
            <p className="text-slate-500 mb-8 leading-relaxed font-sans">
              Our state-of-the-art facility combines traditional coir processing expertise with modern technology
              to produce products that meet the highest quality standards. We are committed to sustainability
              and environmental responsibility in every step of our process.
            </p>
            
            {/* Features list */}
            <div className="grid sm:grid-cols-2 gap-4 mb-8">
              {["Premium Quality Assurance", "Eco-Friendly Production", "Competitive Pricing", "Timely Delivery"].map((item, i) => (
                <div key={i} className="flex items-center gap-3 text-slate-600 font-sans">
                  <div className="w-2 h-2 bg-forest rounded-full" />
                  <span>{item}</span>
                </div>
              ))}
            </div>
            
            <button 
              onClick={() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })} 
              className="btn-primary group"
              data-testid="get-in-touch-btn"
            >
              Get in Touch
            </button>
          </div>
        </div>

        {/* Stats Grid */}
        <div className={`grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mt-20 transition-all duration-1000 delay-500 ${vis ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          {stats.map((stat, i) => (
            <div 
              key={i} 
              className="bg-white p-6 text-center shadow-lg shadow-slate-100/50 hover:shadow-xl transition-all duration-500 group hover:-translate-y-1 card-3d" 
              data-testid={`stat-card-${i}`}
              style={{ transitionDelay: `${i * 100}ms` }}
            >
              <div className={`w-14 h-14 bg-gradient-to-br ${stat.color} rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg group-hover:scale-110 transition-transform duration-500`}>
                <stat.icon className="w-7 h-7 text-white" />
              </div>
              <p className="text-3xl md:text-4xl font-bold text-navy font-serif mb-1 stat-number">{stat.value}</p>
              <p className="text-slate-500 text-sm font-sans">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
